import { useQuery } from "@tanstack/react-query"
import * as O from "fp-ts/Option"
import { pipe } from "fp-ts/function"
import { CalendarX } from "lucide-react"
import { Link } from "react-router-dom"

import { orgPath, useCurrentOrg } from "@/hooks/use-current-org"
import { usePermissions } from "@/hooks/use-permissions"
import {
  closingSentence,
  formatIsoDay,
  organizationTerminationKeys,
} from "@/lib/organization-termination"
import { formatInstantDay } from "@/lib/organisation-day"
import { cn } from "@/lib/utils"
import { getOrganizationTermination } from "@/services/organizationTermination"
import type { OrganizationTermination } from "@/types/api"

// Bandeau des administrateurs : résiliation programmée ou organisme fermé.
// Lien vers la page de résiliation et d'export complet.

function bannerText(termination: OrganizationTermination, timeZone: string): string {
  const effective = formatIsoDay(termination.effective_on)
  const purge = formatInstantDay(termination.purge_after, timeZone)
  return termination.closed
    ? `L'organisme est fermé depuis le ${effective}, sans réouverture possible. Ses données seront effacées définitivement à partir du ${purge}. Téléchargez l'export complet d'ici là.`
    : `Résiliation programmée : l'espace de l'organisme fermera définitivement ${closingSentence(termination.effective_on)}. Ses données seront effacées définitivement à partir du ${purge}.`
}

export function TenantTerminationBanner() {
  const orgSlug = useCurrentOrg()
  const { canRead } = usePermissions()
  const terminationQuery = useQuery({
    queryKey: organizationTerminationKeys.termination,
    queryFn: getOrganizationTermination,
    enabled: canRead("admin"),
  })

  return pipe(
    O.fromNullable(terminationQuery.data),
    O.chain((response) =>
      pipe(
        O.fromNullable(response.termination),
        O.map((termination) => ({ termination, timeZone: response.time_zone })),
      ),
    ),
    O.match(
      () => null,
      ({ termination, timeZone }) => (
        <div
          className={cn(
            "flex flex-wrap items-center gap-3 border-b px-4 py-2 text-sm",
            termination.closed
              ? "border-red-200 bg-red-50 text-red-900 dark:border-red-800 dark:bg-red-950/40 dark:text-red-100"
              : "border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-800 dark:bg-amber-900/30 dark:text-amber-100",
          )}
          data-test="tenant-termination-banner"
          role="status"
        >
          <CalendarX className="size-4 shrink-0" aria-hidden="true" />
          <p className="min-w-0 flex-1">{bannerText(termination, timeZone)}</p>
          <Link
            to={orgPath(orgSlug, "/settings/organization/termination")}
            className="font-medium underline underline-offset-2"
            data-test="tenant-termination-banner-link"
          >
            Voir la résiliation
          </Link>
        </div>
      ),
    ),
  )
}
